import React, {
  ChangeEvent, KeyboardEvent, FC, useEffect, useRef, useState,
} from 'react';
import { ReactComponent as Cross } from '@/assets/icons/close.svg';
import { ReactComponent as Search } from '@/assets/icons/search.svg';
import SearchBarSuggestions, { SearchBarSuggestion }
  from '@/pages/MarketReportsListingPage/components/SearchBar/SearchBarSuggestions/SearchBarSuggestions';
import useDebouncedValue from '@/helpers/useDebouncedValue';
import styles from './SearchBarInput.module.scss';

interface SearchBarInputProps {
  initValue?: string;
  placeholder: string;
  onChange: (value: string) => void;
  isValid: boolean;
  suggestions: SearchBarSuggestion[] | null;
  onClear?: () => void;
  onSuggestionClick?: (placeId: string) => void;
  onSearch?: (placeId: string) => void;
}

const SearchBarInput: FC<SearchBarInputProps> = ({
  onChange, placeholder, initValue, isValid, suggestions, onClear, onSuggestionClick, onSearch,
}: SearchBarInputProps) => {
  const [value, setValue] = useState(initValue);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const debouncedValue = useDebouncedValue(value, 300);

  const onValueChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value;
    setValue(newValue);
  };

  useEffect(() => {
    onChange(debouncedValue);
  }, [debouncedValue]);

  // to open dropdown on value change after clear on cross
  useEffect(() => {
    if (document.activeElement === inputRef.current && !isDropDownOpen) {
      setIsDropDownOpen(true);
    }
  }, [suggestions]);

  useEffect((): () => void => {
    const handleDocumentClick = (event: MouseEvent): void => {
      if (!inputContainerRef || !inputContainerRef.current) {
        return;
      }

      const target = event.target as HTMLElement;
      if ((target === inputContainerRef.current) || inputContainerRef.current.contains(target)) {
        return;
      }

      setIsDropDownOpen(false);
    };
    document.addEventListener('click', handleDocumentClick);

    return (): void => document.removeEventListener('click', handleDocumentClick);
  }, [isDropDownOpen]);

  const onInputFocus = (): void => {
    const element = inputContainerRef.current;
    if (element && !element.classList.contains(styles.focused)) {
      element.classList.add(styles.focused);
    }
    setIsDropDownOpen(true);
  };

  const onInputBlur = (): void => {
    const element = inputContainerRef.current;
    if (element && element.classList.contains(styles.focused)) {
      element.classList.remove(styles.focused);
    }
  };

  const onClearClick = (): void => {
    setValue('');
    if (onClear) {
      onClear();
    }
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.focus();
    }
  };

  const onItemClick = ({ title, placeId }: SearchBarSuggestion): void => {
    setValue(title);
    setIsDropDownOpen(false);
    if (onSuggestionClick) {
      onSuggestionClick(placeId);
    }
  };

  const onSearchClick = (): void => {
    if (!isValid || !suggestions || !suggestions.length) {
      return;
    }
    if (onSearch) {
      setIsDropDownOpen(false);
      onSearch(suggestions[0].placeId);
    }
  };

  const onInputKeyUp = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      onSearchClick();
    }
  };

  return (
    <div className={styles.relativeWrapper}>
      <div ref={inputContainerRef} className={styles.container}>
        <input
          ref={inputRef}
          value={value}
          placeholder={placeholder}
          className={styles.input}
          onChange={onValueChange}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onKeyUp={onInputKeyUp}
        />
        {value && (
          <Cross className={styles.crossIcon} onClick={onClearClick} />
        )}
        <Search className={styles.searchIcon} onClick={onSearchClick} />
        {(value && isValid && suggestions && !!suggestions.length && isDropDownOpen) && (
          <SearchBarSuggestions
            className={styles.suggestions}
            items={suggestions}
            onItemClick={onItemClick}
          />
        )}
      </div>
    </div>
  );
};

export default SearchBarInput;
